import { Countdown } from '@/components/countdown';
import { EarnPpText } from '@/components/earn-pp-text';
import { PointsFaqModal } from '@/components/modals/points-faq';
import { usePPScores } from '@/hooks/usePPScores';
import { LeaderboardRecord, type PPSource } from '@/types';
import { formatNumberToLocale } from '@/utils';
import {
  Box,
  Button,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
  type FlexProps,
} from '@chakra-ui/react';
import { FaCaretUp, FaRegClock } from 'react-icons/fa6';

import { FiArrowUpRight } from 'react-icons/fi';

const PP_SOURCES: Record<PPSource, string> = {
  trades: 'Trades',
  tvl: 'TVL',
  referrals: 'Referrals',
  staking: 'Plume Staking',
  volume: 'Volume',
  quests: 'Quests',
  'daily-spin': 'Daily Spin',
  'protocol-interactions': 'Protocol Interactions',
  bridging: 'Bridging',
  'sky-society': 'Sky Society',
};

interface PPSourcesProps extends FlexProps {
  label: string;
  sources: PPSource[];
  stats?: LeaderboardRecord;
}

const PPSources = ({ label, sources, ...props }: PPSourcesProps) => {
  const isDesktop = useBreakpointValue({ base: false, md: true });

  if (!Array.isArray(sources) || sources.length === 0) {
    return null;
  }

  // this is hardcoded b/c we stripped that functionality for now
  const hasGlobalSources = false;
  const useNoGlobalSourcesLayout = isDesktop && !hasGlobalSources;

  return (
    <Flex direction="column" gap={2} flex={useNoGlobalSourcesLayout ? 3 : 1} {...props}>
      <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight={500} color="GrayText">
        {label}
      </Text>
      <Flex direction={useNoGlobalSourcesLayout ? 'row' : { base: 'row', md: 'column' }} gap={3} flexWrap="wrap">
        {sources.map((source, index) => (
          <Flex
            alignItems="center"
            gap={2}
            key={`${source}-${index}`}
            p={useNoGlobalSourcesLayout ? 2 : 0}
            pr={useNoGlobalSourcesLayout ? 4 : 0}
            bg={useNoGlobalSourcesLayout ? '#F9F9F9' : 'transparent'}
            borderRadius={useNoGlobalSourcesLayout ? 'full' : '0'}
          >
            <Image src={`/assets/profile/pp-sources/${source}.svg`} boxSize={useNoGlobalSourcesLayout ? 10 : 6} />
            <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight={{ base: 400, md: 500 }}>
              {PP_SOURCES[source]}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export const PPCard = ({ address }: { address: `0x${string}` }) => {
  const { ppScores, pointsDelta, isLoading } = usePPScores(address);

  const isDesktop = useBreakpointValue({ base: false, md: true });
  // const hasGlobalSources = Array.isArray(stats?.topGlobalPPsources) && stats?.topGlobalPPsources?.length > 0;

  // this is hardcoded b/c we stripped that functionality for now
  const hasGlobalSources = false;
  const useNoGlobalSourcesLayout = isDesktop && !hasGlobalSources;

  if (isLoading || !ppScores) {
    // TODO: ADD SKELETON?
    return <Spinner />;
  }

  if (isDesktop) {
    return (
      <Card.Root borderRadius="3xl" mb={4} mt={6} fontFamily="Lufga, sans-serif" p={8}>
        <Card.Header borderBottomWidth="thin" p={0} pb={8}>
          <HStack gap={4}>
            <Image src="/assets/profile/pp.svg" boxSize={58} />
            <Box flex={1}>
              <Heading color="fg" size="4xl">
                <FormatNumber value={ppScores.activeXp.totalXp} /> PP
              </Heading>
              <Text color="fg.muted" fontWeight="500" fontFamily="body">
                <Text as="span" fontWeight="700" color="fg">
                  <FormatNumber value={ppScores.activeXp.userSelfXp} />
                </Text>{' '}
                Activity PP |{' '}
                <Text as="span" fontWeight="700" color="fg">
                  <FormatNumber value={ppScores.activeXp.referralBonusXp} />
                </Text>{' '}
                Referral PP
              </Text>
            </Box>
            {pointsDelta && pointsDelta > 0 ? (
              <Flex
                alignItems="center"
                bgImage="url(/assets/profile/pp-badge-bg.svg)"
                borderRadius="full"
                px={8}
                py={5}
                bgSize="cover"
                bgRepeat="no-repeat"
                gap={2}
              >
                <Icon as={FaCaretUp} color="#00D594" boxSize={5} />
                <Text fontSize="xl" fontWeight={700} color="#00D594">
                  +<FormatNumber value={pointsDelta} /> PP{' '}
                  <Text as="span" color="fg.muted">
                    since yesterday
                  </Text>
                </Text>
              </Flex>
            ) : null}
          </HStack>
        </Card.Header>
        <Card.Body p={0}>
          <HStack
            alignItems="flex-start"
            justifyContent={useNoGlobalSourcesLayout ? 'center' : 'space-between'}
            w="full"
            borderBottomWidth="thin"
            py={8}
            borderColor="border"
            gap={3}
          >
            {/* Countdown */}
            <Flex direction="column" gap={2} flex={1}>
              {import.meta.env.VITE_PP_COMING_SOON === 'true' ? (
                <Text fontSize="xl" fontWeight={700} color="GrayText" fontStyle="italic">
                  Plume Points update coming soon...
                  <Spinner size="sm" />
                </Text>
              ) : (
                <>
                  <Text fontSize="lg" fontWeight={500} color="GrayText">
                    Points Update
                  </Text>
                  <Countdown fontSize="3xl" fontWeight={700} />
                  <Text fontSize="md" fontWeight={500} color="GrayText">
                    Daily at midnight UTC
                  </Text>
                </>
              )}
            </Flex>
            {/* Personal PP Sources */}
            <PPSources label="Your top PP sources yesterday" sources={ppScores.top3PointsDeltasStrings} />
            {/* Global PP Sources */}
            <PPSources label="Global top PP sources yesterday" sources={[]} />
          </HStack>
          <Flex color="fg.muted" gap={4} pt={8} alignItems="center" w="full">
            <EarnPpText />
            <Link
              href="https://discord.gg/plume-network"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="none"
            >
              <Button variant="outline" rounded="full">
                Discord Support
                <Icon as={FiArrowUpRight} boxSize={4} />
              </Button>
            </Link>
            <PointsFaqModal rounded="full" fontSize="sm" />
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Card.Root rounded="24px" mt={6} mb={3} py={6}>
      <Card.Body p={0}>
        <VStack alignItems="flex-start" gap={4}>
          <Flex gap={4} px={6}>
            <Image src="/assets/profile/pp.svg" boxSize="52px" />
            <Flex direction="column" gap={1} justifyContent="center" alignSelf="center">
              <Heading fontSize="2xl" fontWeight="700" lineHeight={1} mb={0}>
                <FormatNumber value={ppScores.activeXp.totalXp} /> PP
              </Heading>
              {pointsDelta && pointsDelta > 0 ? (
                <Flex alignItems="center" gap={1}>
                  <Icon as={FaCaretUp} color="#00D594" boxSize={5} />
                  <Text fontSize="md" fontWeight={500} color="#00D594">
                    +{pointsDelta} PP{' '}
                    <Text as="span" color="fg.muted">
                      since yesterday
                    </Text>
                  </Text>
                </Flex>
              ) : null}
            </Flex>
          </Flex>
          <VStack alignItems="flex-start" gap={1} px={6}>
            <HStack>
              <Text fontSize="md" fontWeight="500" color="text.secondary">
                <Box as="span" color="fg" fontWeight="700">
                  {formatNumberToLocale(ppScores.activeXp.userSelfXp)}
                </Box>{' '}
                Activity PP
              </Text>
              |
              <Text fontSize="md" fontWeight="500" color="text.secondary">
                <Box as="span" color="fg" fontWeight="700">
                  {formatNumberToLocale(ppScores.activeXp.referralBonusXp)}
                </Box>{' '}
                Referral PP
              </Text>
            </HStack>
          </VStack>
          <Box borderTopWidth={1} borderColor="border" borderTopStyle="solid" w="full" />
          <VStack alignItems="flex-start" gap={1} px={6}>
            <Text fontSize="md" fontWeight="500" color="text.secondary">
              Points refresh every midnight UTC
            </Text>
            <HStack alignItems="center" gap={1}>
              <Icon as={FaRegClock} boxSize={4} />
              <Countdown fontWeight="700" minW={68} />
            </HStack>
          </VStack>
          <Box borderTopWidth={1} borderColor="border" borderTopStyle="solid" w="full" />
          {/* Personal PP Sources */}
          <PPSources label="Your top 3 PP sources yesterday" sources={ppScores.top3PointsDeltasStrings} px={6} />
          <Box borderTopWidth={1} borderColor="border" borderTopStyle="solid" w="full" />
          {/* Global PP Sources */}
          <PPSources label="Global top 3 PP sources yesterday" sources={[]} px={6} />
          {hasGlobalSources && <Box borderTopWidth={1} borderColor="border" borderTopStyle="solid" w="full" />}
          <VStack px={6}>
            <Link
              href="https://discord.gg/plume-network"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="none"
              w="full"
            >
              <Button variant="outline" rounded="full" w="full" fontSize="sm" size="lg">
                Discord Support
                <Icon as={FiArrowUpRight} boxSize={4} />
              </Button>
            </Link>
            <PointsFaqModal mt={2} w="full" rounded="full" fontSize="sm" size="lg" />
            <EarnPpText fontSize="md" textAlign="center" mt={2} />
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
